/**
 * Single source of truth for the technician interface's NON-COLOUR appearance
 * boundary: icon pack, button skin, text face and corner radii.
 *
 * The colour boundary lives in `interfaceThemeContract.js`, whose buckets are
 * compared against Vuetify's installed theme defaults. Appearance is a
 * different surface and cannot live in those buckets: none of these is a
 * Vuetify theme token. They are the office branding layer's own DOM output —
 * the `--brand-*` custom properties and the `data-*` attributes that
 * `stores/branding.js::applyToDom` writes on the document root. Declaring them
 * here gives appearance the same "did we forget one?" property the palette
 * contract gives colours: `interfaceAppearanceBridge.test.js` asserts the
 * bridge writes exactly the declared targets and never an office-inherited one.
 *
 * INTERFACE_OWNED — a `theme_config` field a preset may declare, keyed by its
 *   path in `theme_config`. Present and valid: the bridge writes `target` and
 *   re-asserts it after every branding pass. ABSENT: the office value stands.
 *   That inheritance rule governs the whole appearance layer — a preset opts
 *   in field by field, and declaring nothing is indistinguishable from having
 *   no preset at all.
 * INTERFACE_DERIVED — runtime markers derived from an owned field. They are
 *   not theme_config inputs and the office branding store never writes them.
 * OFFICE_INHERITED — branding output a preset may never claim, with the reason
 *   it belongs to the office or to the user instead.
 */
export const INTERFACE_APPEARANCE_CONTRACT = {
  INTERFACE_OWNED: {
    icon_set: {
      kind: 'dataset',
      target: 'data-icon-style',
      description: 'Preset icon pack, mapped onto the office icon-style vocabulary.',
    },
    button_skin: {
      kind: 'dataset',
      target: 'data-skin',
      description: 'Preset button skin; "classic" clears the office premium skin.',
    },
    font_family: {
      kind: 'style',
      target: '--brand-font-primary',
      description: 'Preset UI text face, so a design pack is not bound to the office body font.',
    },
    'radius.sm': {
      kind: 'style',
      target: '--brand-radius-sm',
      description: 'Preset small corner radius (chips, inputs).',
    },
    'radius.md': {
      kind: 'style',
      target: '--brand-radius-md',
      description: 'Preset medium corner radius (buttons, cards).',
    },
    'radius.lg': {
      kind: 'style',
      target: '--brand-radius-lg',
      description: 'Preset large corner radius (sheets, dialogs).',
    },
  },
  INTERFACE_DERIVED: {
    'data-interface-font-active': (
      'Marks document-wide font inheritance while a preset owns font_family.'
    ),
    'data-interface-radius-active': (
      'Marks that a preset owns at least one radius step, so the components '
      + 'whose Vuetify rounding default was dropped read the token.'
    ),
  },
  OFFICE_INHERITED: {
    '--brand-font-base-size': 'Office base type size; a preset scales through its own layout, not the reader base.',
    '--brand-font-scale': 'Personal accessibility scale (font_scale_override); a preset must not fight the user.',
    '--brand-anim-duration': 'Office motion timing, not preset identity.',
    '--brand-anim-easing': 'Office motion curve, not preset identity.',
    '--brand-text-primary': 'Text colour belongs to the palette contract, not the appearance layer.',
    '--brand-text-secondary': 'Text colour belongs to the palette contract, not the appearance layer.',
    'data-density': 'The office data-density attribute has no CSS or JS consumer, so a preset density would need a real density mechanism first.',
    'data-theme': 'Light or dark mode is a domain and user decision, not a preset one.',
    'data-brand-version': 'Office branding cache version.',
  },
}

/** Convert a `data-*` attribute name into its HTMLElement.dataset key. */
export function datasetKeyForAttribute(attribute) {
  return String(attribute)
    .replace(/^data-/, '')
    .replace(/-([a-z])/g, (_match, character) => character.toUpperCase())
}

/** Declared targets of one kind, so the bridge never repeats a literal name. */
export function interfaceAppearanceTargets(kind) {
  return Object.fromEntries(
    Object.entries(INTERFACE_APPEARANCE_CONTRACT.INTERFACE_OWNED)
      .filter(([, spec]) => spec.kind === kind)
      .map(([field, spec]) => [field, spec.target]),
  )
}
